import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CreditCard, Trash2, Star, StarOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Setup Card modal — customer self-service.
 *
 * Each hotel owner manages the cards for the hotels THEY own from the
 * customer Dashboard → Settings tab. Admins/managers no longer add or
 * remove cards on the customer's behalf; the admin Hotels tab only
 * shows a grey/green "Card on file" indicator, not the card details.
 *
 * A hotel must always have at least one card on file. To change cards
 * the customer adds a new one first, then removes the old one — the
 * server refuses to detach the last remaining card.
 *
 * Flow:
 *   1. GET /api/billing/hotels/{id}/setup-intent → { client_secret, publishable_key }
 *   2. Mount Stripe Elements bound to that SetupIntent
 *   3. On confirm success, PaymentMethod is auto-attached to the Customer
 *      (by SetupIntent's `customer` param), and the PM is set as default
 *      via POST /payment-methods/{pm}/default.
 *   4. List refreshes to show the newly saved card.
 */
export default function SetupCardDialog({ open, onOpenChange, hotel }) {
  const [initializing, setInitializing] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [pms, setPms] = useState([]);
  const [defaultPm, setDefaultPm] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  // T&C consent — must be ticked before adding or changing cards.
  const [agreed, setAgreed] = useState(false);
  const [terms, setTerms] = useState("");
  const [termsOpen, setTermsOpen] = useState(false);

  const reloadList = useCallback(async () => {
    if (!hotel?.id) return;
    setLoadingList(true);
    try {
      const res = await axios.get(`${API}/billing/hotels/${hotel.id}/payment-methods`);
      setPms(res.data.payment_methods || []);
      setDefaultPm(res.data.default_pm || null);
    } catch (e) {
      toast.error("Couldn't load saved cards");
    } finally {
      setLoadingList(false);
    }
  }, [hotel?.id]);

  const startSetup = useCallback(async () => {
    if (!hotel?.id) return;
    setInitializing(true);
    try {
      const res = await axios.post(`${API}/billing/hotels/${hotel.id}/setup-intent`);
      setClientSecret(res.data.client_secret);
      setStripePromise(loadStripe(res.data.publishable_key));
    } catch (e) {
      toast.error(`Failed to start setup: ${e.response?.data?.detail || e.message}`);
    } finally {
      setInitializing(false);
    }
  }, [hotel?.id]);

  useEffect(() => {
    if (open) {
      reloadList();
      setClientSecret(null); // reset the form each open
      setAgreed(false); // consent must be given each time
      axios.get(`${API}/terms?kind=card`).then((r) => setTerms(r.data?.content || "")).catch(() => {});
    }
  }, [open, reloadList]);

  const makeDefault = async (pmId) => {
    if (!agreed) {
      toast.error("Please accept the Terms & Conditions first");
      return;
    }
    try {
      await axios.post(`${API}/billing/hotels/${hotel.id}/payment-methods/${pmId}/default`);
      toast.success("Default card updated");
      reloadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const detach = async (pmId) => {
    // Client-side "at least one card" guard for UX polish — the server
    // enforces the same rule server-side (see billing.py delete_pm).
    if (pms.length <= 1) {
      toast.error("Add a new card first — you must keep at least one card on file.");
      return;
    }
    if (!window.confirm("Remove this card?")) return;
    try {
      await axios.delete(`${API}/billing/hotels/${hotel.id}/payment-methods/${pmId}`);
      toast.success("Card removed");
      reloadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="setup-card-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payment cards — {hotel?.hotel_name}
          </DialogTitle>
          <DialogDescription>
            Add a new card first, then you can remove an old one. At least one
            card must remain on file.
          </DialogDescription>
        </DialogHeader>

        {/* Existing cards */}
        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Saved cards</div>
          {loadingList ? (
            <div className="py-4 text-center text-sm text-neutral-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : pms.length === 0 ? (
            <div className="py-4 text-center text-sm text-neutral-500">No saved cards</div>
          ) : (
            <div className="border rounded-md divide-y">
              {pms.map((pm) => (
                <div key={pm.id} className="flex items-center justify-between p-2" data-testid={`pm-row-${pm.id}`}>
                  <div className="flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-neutral-500" />
                    <div>
                      <div className="text-sm font-medium">
                        {pm.brand.toUpperCase()} •••• {pm.last4}
                      </div>
                      <div className="text-xs text-neutral-500">
                        Expires {String(pm.exp_month).padStart(2, "0")}/{pm.exp_year}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {defaultPm === pm.id ? (
                      <span className="text-xs px-2 py-1 rounded-sm bg-amber-100 text-amber-800 flex items-center gap-1">
                        <Star className="h-3 w-3" /> Default
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => makeDefault(pm.id)}
                        data-testid={`pm-default-${pm.id}`}
                      >
                        <StarOff className="h-3 w-3 mr-1" /> Set default
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => detach(pm.id)}
                      disabled={pms.length <= 1}
                      title={pms.length <= 1 ? "Add a new card first — at least one card must remain on file" : "Remove this card"}
                      data-testid={`pm-delete-${pm.id}`}
                    >
                      <Trash2 className={"h-3 w-3 " + (pms.length <= 1 ? "text-neutral-300" : "text-red-600")} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add a new card */}
        <div className="pt-2 space-y-3">
          <div className="flex items-start gap-2.5">
            <Checkbox
              id="card-terms-agree"
              data-testid="card-terms-checkbox"
              checked={agreed}
              onCheckedChange={(v) => setAgreed(!!v)}
              className="mt-0.5"
            />
            <label htmlFor="card-terms-agree" className="text-xs text-neutral-500 leading-relaxed select-none">
              I agree to the{" "}
              <button
                type="button"
                data-testid="card-terms-link"
                onClick={() => setTermsOpen(true)}
                className="text-brand-navy font-semibold underline underline-offset-2"
              >
                Terms &amp; Conditions
              </button>{" "}
              for saving and charging this card
            </label>
          </div>
          {!clientSecret ? (
            <Button
              onClick={startSetup}
              disabled={initializing || !agreed}
              className="w-full bg-brand-navy hover:bg-brand-navy/90 text-white rounded-sm"
              data-testid="setup-card-start"
              title={agreed ? undefined : "Accept the Terms & Conditions first"}
            >
              {initializing ? <Loader2 className="h-4 w-4 animate-spin" /> : "+ Add a new card"}
            </Button>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
              <CardEntryForm
                hotelId={hotel.id}
                clientSecret={clientSecret}
                onSuccess={() => {
                  toast.success("Card saved");
                  setClientSecret(null);
                  reloadList();
                  // Record DDR consent + email receipt to the operator (fire & forget).
                  axios.post(`${API}/billing/hotels/${hotel.id}/ddr-consent`).catch(() => {});
                }}
                onCancel={() => setClientSecret(null)}
              />
            </Elements>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="setup-card-close">Close</Button>
        </DialogFooter>
      </DialogContent>

      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="card-terms-dialog">
          <DialogHeader>
            <DialogTitle className="text-brand-navy">Terms &amp; Conditions</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-neutral-700 whitespace-pre-wrap leading-relaxed" data-testid="card-terms-content">
            {terms || "No terms and conditions have been published yet."}
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

function CardEntryForm({ hotelId, clientSecret, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
    });
    if (error) {
      toast.error(error.message || "Card entry failed");
      setSubmitting(false);
      return;
    }
    if (setupIntent?.payment_method) {
      try {
        await axios.post(`${API}/billing/hotels/${hotelId}/payment-methods/${setupIntent.payment_method}/default`);
      } catch (e) {
        toast.error("Card saved but couldn't set as default");
      }
    }
    setSubmitting(false);
    onSuccess?.();
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <PaymentElement />
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} className="flex-1 rounded-sm">Cancel</Button>
        <Button
          type="submit"
          disabled={!stripe || submitting}
          className="flex-1 bg-brand-navy hover:bg-brand-navy/90 text-white rounded-sm"
          data-testid="setup-card-submit"
        >
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save card"}
        </Button>
      </div>
    </form>
  );
}
